<template>
  <component :is="layout" v-if="!games.loading && !userFetching">
    <router-view />
    <BxToast />
  </component>
</template>
<script lang="ts">
import { defineComponent, ref } from "vue"
import DefaultLayout from "@/layouts/DefaultLayout.vue"
import EmptyLayout from "@/layouts/EmptyLayout.vue"
import MobileLayout from "@/layouts/MobileLayout.vue"
import { useWindowSize } from "@/composables/useWindowSize"
import { AsyncState, UserData } from "./store/types"
import { Game } from "./services/types"
import { callApi } from "./services/api"
import { useLocalStorage } from "@vueuse/core"
import { GiveawaySocketService } from "./services/socket/GiveawaySocketService"
import { GlobalSocketService } from "./services/socket/GlobalSocketService"
import AdminLayout from "./layouts/AdminLayout.vue"
import ModeratorLayout from "./layouts/ModeratorLayout.vue"
import BxToast from "./components/uikit/BxToast.vue"

export default defineComponent({
  name: "App",
  components: {
    DefaultLayout,
    EmptyLayout,
    MobileLayout,
    AdminLayout,
    ModeratorLayout,
    BxToast,
  },
  setup() {
    const { isTablet, isMobile } = useWindowSize()

    const userProfile = useLocalStorage("user", null)
    const accessToken = useLocalStorage("access_token", null)

    const chatBlockedTimeout = ref<number | null>(null)
    const userFetching = ref(true)
    return {
      isTablet,
      isMobile,
      userProfile: userProfile.value ? JSON.parse(userProfile.value) : null,
      accessToken,
      chatBlockedTimeout,
      userFetching,
    }
  },
  watch: {
    isMobileOrTablet: {
      handler(newVal) {
        this.fixAsideWidth(newVal)
      },
      immediate: true,
    },
    globalChatBlocked(isChatBlocked: boolean) {
      if (
        isChatBlocked &&
        this.globalChatBlockDuration &&
        !this.chatBlockedTimeout
      ) {
        this.chatBlockedTimeout = setTimeout(() => {
          this.$privateSocket.emit("joinGlobalChat")
          this.$store.commit("global/UNSET_CHAT_BLOCKED")
        }, this.globalChatBlockDuration * 1000)
      } else if (this.chatBlockedTimeout) {
        clearTimeout(this.chatBlockedTimeout)
      }
    },
  },
  computed: {
    isMobileOrTablet(): boolean {
      return this.isMobile || this.isTablet
    },
    games(): AsyncState<Game[]> {
      return this.$store.getters["games/data"] as AsyncState<Game[]>
    },
    globalChatBlocked(): boolean {
      return this.$store.getters["global/globalChatBlocked"]
    },
    globalChatBlockDuration(): number {
      return this.$store.getters["global/globalChatBlockDuration"]
    },
    authUser(): UserData {
      return this.$store.getters["auth/user"]
    },
    layout() {
      if (this.authUser.is_moderator) {
        return ModeratorLayout
      }

      if (this.$route.path.startsWith("/admin")) {
        return AdminLayout
      }

      if (this.$route.name === "callback") {
        return EmptyLayout
      }

      if (this.isMobileOrTablet) {
        return MobileLayout
      }

      return DefaultLayout
    },
  },
  async mounted() {
    await this.$store.dispatch("global/getConfigs")
    await this.$store.dispatch("games/getGames")
    this.fixAsideWidth(this.isMobileOrTablet)
    this.$store.commit("auth/SET_FETCHING_PROFILE", this.getAuthUser())
    // this.$publicSocket.connect()

    // console.log(this.access_token)
    // if (!this.isPrivateSocketConnected && this.access_token) {
    //   console.log("Connecting privateSocket... not connected")
    //   this.$privateSocket.connect()
    // }

    // if (this.userProfile) {
    //   this.$store.dispatch("auth/setUser", this.userProfile)
    // }

    this.initializeSocketListeners()

    this.$nextTick(() => {
      this.$publicSocket.emit("joinGiveaway")
      this.$publicSocket.emit("joinGlobal")
    })

    new GiveawaySocketService(this.$publicSocket)
    new GlobalSocketService(this.$publicSocket)
  },
  beforeUnmount() {
    this.$publicSocket.emit("leaveGiveaway")
    this.$publicSocket.emit("leaveGlobal")
    this.$publicSocket.disconnect()
    this.$privateSocket.disconnect()
  },
  methods: {
    initializeSocketListeners() {
      this.$publicSocket.on("newGlobalChatMessage", (message) => {
        this.$store.commit("global/ADD_MESSAGE", message)
      })
      this.$privateSocket.on("chat:history", (messages) => {
        this.$store.commit("global/SET_MESSAGES", messages)
      })
      this.$publicSocket.on("chat:history", (messages) => {
        this.$store.commit("global/SET_MESSAGES", messages)
      })
      this.$privateSocket.on("newGlobalChatMessage", (message) => {
        this.$store.commit("global/ADD_MESSAGE", message)
      })
      this.$publicSocket.on("chat:pinned", ({ message }) => {
        this.$store.commit("global/SET_PINNED_MESSAGE", message)
      })
      this.$publicSocket.on("chat:unpinned", (message) => {
        this.$store.commit("global/UNSET_PINNED_MESSAGE", message)
      })
      this.$privateSocket.on("chat:pinned:init", ({ messages }) => {
        this.$store.commit("global/SET_PINNED_MESSAGES", messages)
      })
      this.$publicSocket.on("chat:pinned:init", ({ messages }) => {
        this.$store.commit("global/SET_PINNED_MESSAGES", messages)
      })
      this.$publicSocket.on("chat:deleted", ({ messageId }) => {
        this.$store.commit("global/DELETE_MESSAGE", messageId)
      })
      this.$privateSocket.on("chat:error", (chatError) => {
        console.log("chat:error")
        this.$store.commit("global/ADD_CHAT_ERROR", chatError)
      })
      this.$privateSocket.on("chat:blocked", ({ duration, message }) => {
        console.log("block duration: ", duration)

        this.$store.commit("global/CHAT_BLOCKED", {
          duration,
          message: { message },
        })
      })
      this.$privateSocket.on("chat:unblocked", () => {
        this.$privateSocket.emit("joinGlobalChat")
        this.$store.commit("global/UNSET_CHAT_BLOCKED")
      })
      this.$publicSocket.on("onlineUsersCount", (data) => {
        this.$store.commit("global/UPDATE_ONLINE_USERS_COUNT", data.count)
      })
    },
    async getAuthUser() {
      if (this.accessToken) {
        try {
          const {
            data: { data: userInfo },
          } = await callApi.getUserInfo()
          this.$store.dispatch("auth/setUser", userInfo)
          this.userProfile = JSON.stringify(userInfo)
          this.userFetching = false
        } catch (error) {
          console.error(error)
        }
      } else {
        this.userFetching = false
      }
    },
    fixAsideWidth(isMobile: boolean) {
      if (isMobile) {
        document.documentElement.style.setProperty("--left-aside-width", "0px")
      } else {
        document.documentElement.style.setProperty(
          "--left-aside-width",
          "214px"
        )
      }
    },
  },
})
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  position: relative;
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
</style>
